import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, ExternalLink, MapPin, X, ZoomIn } from 'lucide-react';
import PortfolioImage from '../components/PortfolioImage';
import PublicPageShell from '../components/PublicPageShell';
import NotFound from './NotFound';
import { useLanguage } from '../contexts/LanguageContext';
import { getLocalizedPublicCaseStudyBySlug } from '../data/contentLibrary';
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '../utils/analytics';

const ProjectBadge = ({ type, children }) => (
  <span
    className={`inline-flex rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
      type === 'in-house'
        ? 'border-[#00FFD1]/35 bg-[#00FFD1]/10 text-[#7fffe8]'
        : 'border-white/15 bg-white/[0.045] text-white/62'
    }`}
  >
    {children}
  </span>
);

const CaseStudyPage = () => {
  const { slug } = useParams();
  const { currentLang, t } = useLanguage();
  const caseStudy = getLocalizedPublicCaseStudyBySlug(slug, currentLang);
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    if (caseStudy) {
      trackAnalyticsEvent(ANALYTICS_EVENTS.CASE_STUDY_VIEWED, {
        caseStudySlug: caseStudy.slug,
      });
    }
  }, [caseStudy]);

  useEffect(() => {
    if (!activeImage) return undefined;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setActiveImage(null);
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [activeImage]);

  if (!caseStudy) return <NotFound />;

  return (
    <PublicPageShell mainClassName="pt-20">
      <article className="relative overflow-hidden bg-black">
        <div className="hero-grid absolute inset-0 pointer-events-none" />
        <div className="relative mx-auto max-w-[1120px] px-5 py-16 md:px-[7.6923%] md:py-24">
          <nav
            className="mb-8 flex flex-wrap gap-2 text-sm text-white/44"
            aria-label={t('common.breadcrumb')}
          >
            <Link to="/" className="hover:text-[#00FFD1]">{t('nav.home')}</Link>
            <span>/</span>
            <Link to="/work" className="hover:text-[#00FFD1]">Work</Link>
            <span>/</span>
            <span className="text-white/70">{caseStudy.title}</span>
          </nav>

          <header className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-3">
              <ProjectBadge type={caseStudy.projectType}>{caseStudy.badge}</ProjectBadge>
              <span className="flex items-center gap-1.5 text-xs text-white/42">
                <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                {caseStudy.industry} · {caseStudy.city}
              </span>
            </div>
            <h1 className="mt-6 text-4xl font-semibold leading-tight text-white md:text-6xl">
              {caseStudy.title}
            </h1>
            <p className="mt-6 text-base leading-8 text-white/64 md:text-lg">
              {caseStudy.summary}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              {caseStudy.demoUrl ? (
                <a
                  href={caseStudy.demoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-glass-primary inline-flex items-center gap-2"
                >
                  {caseStudy.demoButtonLabel}
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </a>
              ) : (
                <span className="inline-flex items-center rounded-xl border border-white/10 px-5 py-3 text-sm text-white/38">
                  Demo link coming soon
                </span>
              )}
              <Link to="/work" className="btn-glass-secondary inline-flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                All work
              </Link>
            </div>
          </header>

          <section className="mt-14" aria-labelledby="gallery-heading">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#00FFD1]">
                  Product tour
                </p>
                <h2 id="gallery-heading" className="mt-2 text-3xl font-semibold text-white">
                  Screenshots
                </h2>
              </div>
              <p className="hidden text-xs text-white/38 sm:block">Select an image to enlarge it</p>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {caseStudy.screenshots.map((screenshot) => (
                <button
                  key={screenshot.src}
                  type="button"
                  onClick={() => setActiveImage(screenshot)}
                  className="group relative overflow-hidden rounded-xl border border-white/[0.08] text-left transition-colors hover:border-[#00FFD1]/35 focus:outline-none focus:ring-2 focus:ring-[#00FFD1]/70"
                  aria-label={`Enlarge ${screenshot.label}`}
                >
                  <PortfolioImage image={screenshot} />
                  <span className="absolute bottom-3 left-3 right-3 flex items-center justify-between rounded-lg bg-black/75 px-3 py-2 text-xs text-white/72 backdrop-blur">
                    {screenshot.label}
                    <ZoomIn className="h-4 w-4 text-[#00FFD1]" aria-hidden="true" />
                  </span>
                </button>
              ))}
            </div>
          </section>

          <div className="mt-16 grid gap-8 lg:grid-cols-3">
            <section className="border-t border-white/[0.09] pt-7">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#00FFD1]">01</p>
              <h2 className="mt-3 text-2xl font-semibold text-white">Problem</h2>
              <p className="mt-4 text-sm leading-7 text-white/60">{caseStudy.problem}</p>
            </section>

            <section className="border-t border-white/[0.09] pt-7">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#00FFD1]">02</p>
              <h2 className="mt-3 text-2xl font-semibold text-white">Approach</h2>
              <ul className="mt-4 space-y-3">
                {caseStudy.approach.map((item) => (
                  <li key={item} className="flex gap-3 text-sm leading-6 text-white/60">
                    <Check className="mt-0.5 h-4 w-4 flex-none text-[#00FFD1]" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="border-t border-white/[0.09] pt-7">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#00FFD1]">03</p>
              <h2 className="mt-3 text-2xl font-semibold text-white">Result</h2>
              <p className="mt-4 text-sm leading-7 text-white/60">{caseStudy.result}</p>
            </section>
          </div>

          <section className="mt-14 border-t border-white/[0.09] pt-8">
            <h2 className="text-2xl font-semibold text-white">Technology</h2>
            <p className="mt-2 text-sm text-white/45">
              The tools are secondary to the experience, but this build uses:
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {caseStudy.technologies.map((technology) => (
                <span
                  key={technology}
                  className="rounded-full border border-white/[0.09] bg-white/[0.025] px-3 py-1.5 text-xs text-white/52"
                >
                  {technology}
                </span>
              ))}
            </div>
          </section>

          <aside className="mt-16 rounded-2xl border border-[#00FFD1]/20 bg-[#00FFD1]/[0.045] p-7 md:flex md:items-center md:justify-between md:gap-8 md:p-10">
            <div>
              <h2 className="text-2xl font-semibold text-white md:text-3xl">
                Want something like this for your business?
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-white/58">
                Tell me what your customers need to do and where the current process gets in their way.
              </p>
            </div>
            <Link to="/contact" className="btn-glass-primary mt-6 inline-flex items-center gap-2 md:mt-0">
              Discuss your project
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </aside>
        </div>
      </article>

      {activeImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm md:p-10"
          role="dialog"
          aria-modal="true"
          aria-label={`${activeImage.label} enlarged screenshot`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setActiveImage(null);
          }}
        >
          <div className="relative w-full max-w-6xl">
            <button
              type="button"
              onClick={() => setActiveImage(null)}
              className="absolute -top-12 right-0 inline-flex items-center gap-2 text-sm text-white/65 hover:text-white"
              aria-label="Close enlarged screenshot"
            >
              Close <X className="h-5 w-5" aria-hidden="true" />
            </button>
            <PortfolioImage image={activeImage} className="rounded-xl border border-white/10" />
            <p className="mt-3 text-center text-sm text-white/55">{activeImage.label}</p>
          </div>
        </div>
      )}
    </PublicPageShell>
  );
};

export default CaseStudyPage;
